/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * DataChangesView — admin-only dashboard showing how the voter roll
 * shifted between snapshots.
 *
 * Backed by the `/admin/data-changes/*` endpoints (see
 * `src/api/dataChanges.ts`). Tenant scope + ADMIN role are enforced
 * server-side; this view also runs an access guard locally as
 * defense-in-depth — a stale token could otherwise slip past the
 * nav filter.
 *
 * Layout (top-down):
 *   1. Access guard (red card if not ADMIN)
 *   2. Header with import-batch picker + (optional) Compare-to picker
 *   3. Voter lookup search input (top-of-view)
 *   4. Cross-batch compare banner (only when "Compare to" is set)
 *   5. Stat-card row (4 cards: added / removed / modified / elections)
 *   6. Top-fields horizontal bar chart (Recharts)
 *   7. Status transitions matrix (CSS-grid 2x2 heatmap)
 *   8. Party transitions stacked bar (Recharts)
 *   9. Elections added table (sortable: Date / Type / Voter count, default
 *      voter_count desc so headline elections lead the long retroactive tail)
 *  10. DataChangesMap — geographic heatmap of modified voters per precinct
 *  11. Voter delta drill-down: search + filters + sortable table + paging
 *      + CSV export, with click-name-opens-drawer
 *
 * Filter, sort, q, page, batch and compare state are mirrored into
 * `window.location.hash` so refresh / link-share preserves view state.
 *
 * Pattern: plain axios + useState + useEffect with cancellation
 * flags, mirroring DropOffView. NOT React Query.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  LabelList,
} from 'recharts';
import { GitCompare, Download, Search } from 'lucide-react';
import {
  fetchImportBatches,
  fetchChangeSummary,
  fetchTransitions,
  fetchElectionsAdded,
  fetchVoterDeltas,
  fetchVoterDeltaHistory,
  fetchBatchComparison,
  fetchVoterDeltasCsv,
  buildVoterDeltaParams,
  type ImportBatchRead,
  type ChangeSummaryRead,
  type TransitionRead,
  type ElectionAddedRead,
  type VoterDeltaRead,
  type ListEnvelope,
  type DeltaKind,
  type VoterDeltaSortKey,
  type BatchComparison,
} from '@/api/dataChanges';
import { Skeleton, StatsGridSkeleton } from '@/components/Skeleton';
import EmptyState from '@/components/EmptyState';
import {
  FilterBar,
  FilterMultiSelect,
  FilterDateRange,
  SearchInput,
} from '@/components/filters';
import InfoTip from '@/components/InfoTip';
import VoterDetailDrawer from '@/components/VoterDetailDrawer';
import { ViewHeader } from '@/components/ViewHeader';
import DataChangesMap from './DataChangesMap';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_VALUES = ['Active', 'Inactive'] as const;

const DELTA_KIND_OPTIONS: Array<{ value: DeltaKind; label: string }> = [
  { value: 'added', label: 'Added' },
  { value: 'removed', label: 'Removed' },
  { value: 'modified', label: 'Modified' },
  { value: 'election_added', label: 'Election Added' },
];

const FIELD_NAME_OPTIONS = [
  { value: 'status', label: 'Status' },
  { value: 'party', label: 'Party' },
  { value: 'address', label: 'Address' },
  { value: 'precinct_num', label: 'Precinct' },
  { value: 'legislative_district', label: 'Legislative District' },
  { value: 'registration_date', label: 'Registration Date' },
  { value: 'birth_year', label: 'Birth Year' },
];

// Recharts series colors keyed off design-system tokens.
const COLOR_FIELD_BAR = 'var(--chart-series-2)'; // blue
const PARTY_PALETTE: Record<string, string> = {
  R: 'var(--chart-series-1)',
  D: 'var(--chart-series-2)',
  I: 'var(--chart-series-4)',
  L: 'var(--chart-series-5)',
  G: 'var(--chart-series-3)',
  Other: 'rgb(100 116 139)', // slate-500
};

// Map elections-table sort keys to a comparator. Keep it explicit so
// switching default sort doesn't break older sort hashes.
type ElectionsSortKey = 'date' | 'type' | 'count';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatObservedAt(iso: string | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatDate(iso: string | undefined): string {
  if (!iso) return '—';
  // election_date arrives as a bare YYYY-MM-DD; constructing new Date() on that
  // parses as UTC midnight, which then displays as the previous day in any
  // negative-offset timezone (Platte County is UTC-5/-6). Parse the parts
  // explicitly so the displayed date matches the source.
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  try {
    const d = dateOnly
      ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
      : new Date(iso);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  } catch {
    return iso;
  }
}

function fieldLabel(name: string | null): string {
  if (!name) return '—';
  const opt = FIELD_NAME_OPTIONS.find((o) => o.value === name);
  return opt ? opt.label : name;
}

function partyColor(value: string): string {
  return PARTY_PALETTE[value] || PARTY_PALETTE.Other;
}

/**
 * Map a count to a Tailwind background class. Higher counts = deeper red.
 * Used for the status transitions heatmap matrix cells.
 */
function intensityClass(count: number, max: number): string {
  if (max <= 0 || count <= 0) return 'bg-bg-raised/40';
  const ratio = count / max;
  if (ratio >= 0.85) return 'bg-accent';
  if (ratio >= 0.6) return 'bg-accent/80';
  if (ratio >= 0.35) return 'bg-status-danger-bg/70';
  if (ratio >= 0.1) return 'bg-status-danger-bg/60';
  return 'bg-bg-elevated/50';
}

// ---------------------------------------------------------------------------
// Hash state encoding/decoding
//
// Format: #data-changes?batch=…&kind=…&field=…&q=…&page=…&sort=…&order=…&compare=…
// Multi-valued filters (kind, field) are comma-joined.
//
// We replace history rather than push so back-button history doesn't fill up
// with filter twiddles.
// ---------------------------------------------------------------------------

interface HashState {
  batch?: string;
  kind?: DeltaKind[];
  field?: string[];
  q?: string;
  page?: number;
  sort?: VoterDeltaSortKey;
  order?: 'asc' | 'desc';
  compare?: string;
}

function readHash(): HashState {
  if (typeof window === 'undefined') return {};
  const raw = window.location.hash || '';
  // Strip leading "#" and optional "data-changes?" prefix.
  const trimmed = raw.replace(/^#/, '');
  if (!trimmed.startsWith('data-changes')) return {};
  const idx = trimmed.indexOf('?');
  if (idx < 0) return {};
  const qs = new URLSearchParams(trimmed.slice(idx + 1));
  const out: HashState = {};
  const batch = qs.get('batch');
  if (batch) out.batch = batch;
  const kind = qs.get('kind');
  if (kind) out.kind = kind.split(',').filter(Boolean) as DeltaKind[];
  const field = qs.get('field');
  if (field) out.field = field.split(',').filter(Boolean);
  const q = qs.get('q');
  if (q) out.q = q;
  const page = qs.get('page');
  if (page && /^\d+$/.test(page)) out.page = Number(page);
  const sort = qs.get('sort');
  if (sort) out.sort = sort as VoterDeltaSortKey;
  const order = qs.get('order');
  if (order === 'asc' || order === 'desc') out.order = order;
  const compare = qs.get('compare');
  if (compare) out.compare = compare;
  return out;
}

function writeHash(state: HashState) {
  if (typeof window === 'undefined') return;
  const qs = new URLSearchParams();
  if (state.batch) qs.set('batch', state.batch);
  if (state.kind && state.kind.length > 0) qs.set('kind', state.kind.join(','));
  if (state.field && state.field.length > 0) qs.set('field', state.field.join(','));
  if (state.q) qs.set('q', state.q);
  if (state.page && state.page > 1) qs.set('page', String(state.page));
  if (state.sort) qs.set('sort', state.sort);
  if (state.order) qs.set('order', state.order);
  if (state.compare) qs.set('compare', state.compare);
  const next = `#data-changes?${qs.toString()}`;
  // replaceState avoids polluting back-button history with filter twiddles.
  if (window.location.hash !== next) {
    window.history.replaceState(null, '', next);
  }
}

// ---------------------------------------------------------------------------
// Main view
// ---------------------------------------------------------------------------

export interface DataChangesViewProps {
  userRole?: string;
  onBack?: () => void;
  onJumpToRoute?: (subregion: string, shiftId: string, stopIndex: number) => void;
}

export function DataChangesView({ userRole, onBack: _onBack, onJumpToRoute }: DataChangesViewProps) {
  // ============================================================================
  // Access guard — defense in depth.
  // Backend may emit lowercase, frontend types expect uppercase.
  // ============================================================================
  const roleNorm = typeof userRole === 'string' ? userRole.toUpperCase() : '';
  if (roleNorm !== 'ADMIN') {
    return (
      <div className="min-h-dvh bg-bg-base text-fg-primary p-6">
        <div className="max-w-2xl mx-auto rounded-lg border border-status-danger-bg/30 bg-status-danger-bg/20 p-6">
          <h2 className="text-lg font-semibold text-status-danger mb-2">Access Denied</h2>
          <p className="text-fg-secondary">
            The Data Changes view is available to administrators only. If you
            believe this is a mistake, contact your tenant admin.
          </p>
        </div>
      </div>
    );
  }

  // Hash state read once on mount; subsequent writes are replaceState-only.
  const initialHash = useRef<HashState>(readHash());

  // ============================================================================
  // Top-level state
  // ============================================================================
  const [batches, setBatches] = useState<ImportBatchRead[] | null>(null);
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(
    initialHash.current.batch ?? null,
  );
  const [compareBatchId, setCompareBatchId] = useState<string | null>(
    initialHash.current.compare ?? null,
  );
  const [comparison, setComparison] = useState<BatchComparison | null>(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);

  const [summary, setSummary] = useState<ChangeSummaryRead | null>(null);
  const [statusTransitions, setStatusTransitions] = useState<TransitionRead[] | null>(null);
  const [partyTransitions, setPartyTransitions] = useState<TransitionRead[] | null>(null);
  const [electionsAdded, setElectionsAdded] = useState<ElectionAddedRead[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadCounter, setReloadCounter] = useState(0);

  // Voter delta drill-down state
  const [voterPage, setVoterPage] = useState<ListEnvelope<VoterDeltaRead> | null>(null);
  const [voterLoading, setVoterLoading] = useState<boolean>(false);
  const [voterError, setVoterError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(initialHash.current.page ?? 1);
  const [pageSize] = useState<number>(50);
  const [deltaKindFilter, setDeltaKindFilter] = useState<DeltaKind[]>(
    initialHash.current.kind ?? [],
  );
  const [fieldNameFilter, setFieldNameFilter] = useState<string[]>(
    initialHash.current.field ?? [],
  );
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [searchQ, setSearchQ] = useState<string>(initialHash.current.q ?? '');
  const [sortKey, setSortKey] = useState<VoterDeltaSortKey>(
    initialHash.current.sort ?? 'observed_at',
  );
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(
    initialHash.current.order ?? 'desc',
  );

  // Elections-added table sort state (client-side; tiny dataset).
  // Default = voter_count desc so the headline elections lead the long
  // tail of 1-voter retroactive entries.
  const [electionsSortKey, setElectionsSortKey] = useState<ElectionsSortKey>('count');
  const [electionsSortOrder, setElectionsSortOrder] = useState<'asc' | 'desc'>('desc');

  // Drawer / per-voter card state
  const [selectedVoterId, setSelectedVoterId] = useState<number | null>(null);
  const [selectedExternalId, setSelectedExternalId] = useState<string | null>(null);
  const [selectedVoterName, setSelectedVoterName] = useState<string | null>(null);
  const [externalHistory, setExternalHistory] = useState<VoterDeltaRead[] | null>(null);
  const [externalHistoryLoading, setExternalHistoryLoading] = useState(false);
  const [externalHistoryError, setExternalHistoryError] = useState<string | null>(null);

  // CSV export state
  const [exporting, setExporting] = useState(false);

  // Top-of-view voter lookup
  const [topLookup, setTopLookup] = useState('');
  const [topLookupBusy, setTopLookupBusy] = useState(false);
  const tableRef = useRef<HTMLDivElement | null>(null);

  // ============================================================================
  // Hash sync — re-write whenever the relevant subset changes.
  // ============================================================================
  useEffect(() => {
    writeHash({
      batch: selectedBatchId ?? undefined,
      kind: deltaKindFilter,
      field: fieldNameFilter,
      q: searchQ || undefined,
      page,
      sort: sortKey,
      order: sortOrder,
      compare: compareBatchId ?? undefined,
    });
  }, [
    selectedBatchId,
    deltaKindFilter,
    fieldNameFilter,
    searchQ,
    page,
    sortKey,
    sortOrder,
    compareBatchId,
  ]);

  // ============================================================================
  // Load import batches once on mount → pick the most recent as default.
  // If hash carried a batch id, prefer that.
  // ============================================================================
  useEffect(() => {
    let cancelled = false;
    fetchImportBatches()
      .then((rows) => {
        if (cancelled) return;
        setBatches(rows);
        if (rows.length === 0) {
          setLoading(false);
          return;
        }
        const sorted = [...rows].sort((a, b) =>
          b.observed_at.localeCompare(a.observed_at),
        );
        if (selectedBatchId && rows.some((r) => r.import_batch_id === selectedBatchId)) {
          // Hash-supplied id is valid; keep it.
          return;
        }
        setSelectedBatchId(sorted[0].import_batch_id);
      })
      .catch((e: any) => {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 403) {
          setError('Your role does not have access to data changes.');
        } else if (status) {
          setError(`Backend error ${status} loading import batches.`);
        } else {
          setError('Could not reach the data-changes backend.');
        }
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================================================
  // When a batch is selected (or reload is forced), fetch summary +
  // transitions + elections in parallel.
  // ============================================================================
  useEffect(() => {
    if (!selectedBatchId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      fetchChangeSummary(selectedBatchId),
      fetchTransitions('status', selectedBatchId),
      fetchTransitions('party', selectedBatchId),
      fetchElectionsAdded(selectedBatchId),
    ])
      .then(([s, st, pt, el]) => {
        if (cancelled) return;
        setSummary(s);
        setStatusTransitions(st);
        setPartyTransitions(pt);
        setElectionsAdded(el);
        setLoading(false);
      })
      .catch((e: any) => {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 403) {
          setError('Your role does not have access to data changes.');
        } else if (status === 404) {
          setError('Data-changes endpoint not found. Backend may not be running.');
        } else if (status) {
          setError(`Backend error ${status}`);
        } else {
          setError('Could not reach the data-changes backend.');
        }
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedBatchId, reloadCounter]);

  // ============================================================================
  // Cross-batch comparison fetch.
  // ============================================================================
  useEffect(() => {
    if (!selectedBatchId || !compareBatchId || compareBatchId === selectedBatchId) {
      setComparison(null);
      return;
    }
    let cancelled = false;
    setComparisonLoading(true);
    fetchBatchComparison(compareBatchId, selectedBatchId)
      .then((res) => {
        if (cancelled) return;
        setComparison(res);
        setComparisonLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        // Comparison is a nice-to-have; don't surface a hard error banner.
        setComparison(null);
        setComparisonLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedBatchId, compareBatchId]);

  // ============================================================================
  // Voter delta drill-down — refetch on filter / page / batch / sort / q.
  // ============================================================================
  useEffect(() => {
    if (!selectedBatchId) return;
    let cancelled = false;
    setVoterLoading(true);
    setVoterError(null);

    fetchVoterDeltas({
      importBatchId: selectedBatchId,
      deltaKind: deltaKindFilter.length > 0 ? deltaKindFilter : undefined,
      fieldName: fieldNameFilter.length > 0 ? fieldNameFilter : undefined,
      q: searchQ || undefined,
      sort: sortKey,
      order: sortOrder,
      page,
      pageSize,
    })
      .then((env) => {
        if (cancelled) return;
        setVoterPage(env);
        setVoterLoading(false);
      })
      .catch((e: any) => {
        if (cancelled) return;
        const status = e?.response?.status;
        setVoterError(
          status ? `Backend error ${status}` : 'Could not load voter deltas.',
        );
        setVoterLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedBatchId, deltaKindFilter, fieldNameFilter, searchQ, sortKey, sortOrder, page, pageSize]);

  // Reset to page 1 whenever filters / search / sort / batch change.
  useEffect(() => {
    setPage(1);
  }, [deltaKindFilter, fieldNameFilter, searchQ, sortKey, sortOrder, selectedBatchId]);

  // ============================================================================
  // Fetch external-id history when the drawer opens for a removed voter.
  // ============================================================================
  useEffect(() => {
    if (!selectedExternalId || selectedVoterId !== null) {
      setExternalHistory(null);
      setExternalHistoryError(null);
      return;
    }
    let cancelled = false;
    setExternalHistoryLoading(true);
    setExternalHistoryError(null);
    fetchVoterDeltaHistory(selectedExternalId)
      .then((rows) => {
        if (cancelled) return;
        setExternalHistory(rows);
        setExternalHistoryLoading(false);
      })
      .catch((e: any) => {
        if (cancelled) return;
        const status = e?.response?.status;
        setExternalHistoryError(
          status ? `Backend error ${status}` : 'Could not load voter history.',
        );
        setExternalHistoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedExternalId, selectedVoterId]);

  // ============================================================================
  // Derived data
  // ============================================================================
  const sortedBatches = useMemo<ImportBatchRead[]>(() => {
    if (!batches) return [];
    return [...batches].sort((a, b) => b.observed_at.localeCompare(a.observed_at));
  }, [batches]);

  const selectedBatch = useMemo(
    () => sortedBatches.find((b) => b.import_batch_id === selectedBatchId) || null,
    [sortedBatches, selectedBatchId],
  );

  // 2x2 status matrix.
  const statusMatrix = useMemo(() => {
    const matrix: Record<string, Record<string, { count: number; reason: string | null }>> = {
      Active: { Active: { count: 0, reason: null }, Inactive: { count: 0, reason: null } },
      Inactive: { Active: { count: 0, reason: null }, Inactive: { count: 0, reason: null } },
    };
    if (!statusTransitions) return matrix;
    for (const t of statusTransitions) {
      const from = matrix[t.from_value] ? t.from_value : null;
      const to = from && matrix[from][t.to_value] ? t.to_value : null;
      if (from && to) {
        matrix[from][to] = { count: t.count, reason: t.change_reason };
      }
    }
    return matrix;
  }, [statusTransitions]);

  const statusMatrixMax = useMemo(() => {
    let max = 0;
    for (const from of STATUS_VALUES) {
      for (const to of STATUS_VALUES) {
        if (statusMatrix[from][to].count > max) max = statusMatrix[from][to].count;
      }
    }
    return max;
  }, [statusMatrix]);

  const partyChartData = useMemo(() => {
    if (!partyTransitions || partyTransitions.length === 0) {
      return { rows: [] as Array<Record<string, any>>, toValues: [] as string[], reasons: {} as Record<string, string | null> };
    }
    const fromMap = new Map<string, Record<string, any>>();
    const toSet = new Set<string>();
    const reasons: Record<string, string | null> = {};
    for (const t of partyTransitions) {
      toSet.add(t.to_value);
      const existing = fromMap.get(t.from_value) || { from_value: t.from_value };
      existing[t.to_value] = (existing[t.to_value] || 0) + t.count;
      reasons[`${t.from_value}->${t.to_value}`] = t.change_reason;
      fromMap.set(t.from_value, existing);
    }
    return {
      rows: Array.from(fromMap.values()),
      toValues: Array.from(toSet),
      reasons,
    };
  }, [partyTransitions]);

  // Elections-added: client-side sort.
  const sortedElectionsAdded = useMemo<ElectionAddedRead[]>(() => {
    if (!electionsAdded) return [];
    const dir = electionsSortOrder === 'asc' ? 1 : -1;
    const copy = [...electionsAdded];
    copy.sort((a, b) => {
      switch (electionsSortKey) {
        case 'date':
          return a.election_date.localeCompare(b.election_date) * dir;
        case 'type':
          return a.election_type.localeCompare(b.election_type) * dir;
        case 'count':
        default:
          return (a.voter_count - b.voter_count) * dir;
      }
    });
    return copy;
  }, [electionsAdded, electionsSortKey, electionsSortOrder]);

  // ============================================================================
  // Handlers
  // ============================================================================
  const toggleSort = useCallback(
    (key: VoterDeltaSortKey) => {
      if (sortKey === key) {
        setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortKey(key);
        setSortOrder('asc');
      }
    },
    [sortKey],
  );

  const toggleElectionsSort = useCallback(
    (key: ElectionsSortKey) => {
      if (electionsSortKey === key) {
        setElectionsSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
      } else {
        setElectionsSortKey(key);
        setElectionsSortOrder(key === 'count' ? 'desc' : 'asc');
      }
    },
    [electionsSortKey],
  );

  const clearFilters = useCallback(() => {
    setDeltaKindFilter([]);
    setFieldNameFilter([]);
    setDateRange([null, null]);
    setSearchQ('');
    setPage(1);
  }, []);

  const openVoter = useCallback((row: VoterDeltaRead) => {
    setSelectedVoterName(row.voter_name || row.voter_id_external);
    if (row.voter_id) {
      setSelectedVoterId(row.voter_id);
      setSelectedExternalId(null);
    } else {
      setSelectedVoterId(null);
      setSelectedExternalId(row.voter_id_external);
    }
  }, []);

  const closeVoter = useCallback(() => {
    setSelectedVoterId(null);
    setSelectedExternalId(null);
    setSelectedVoterName(null);
    setExternalHistory(null);
    setExternalHistoryError(null);
  }, []);

  const handleExportCsv = useCallback(async () => {
    if (!selectedBatchId) return;
    setExporting(true);
    try {
      const blob = await fetchVoterDeltasCsv({
        importBatchId: selectedBatchId,
        deltaKind: deltaKindFilter.length > 0 ? deltaKindFilter : undefined,
        fieldName: fieldNameFilter.length > 0 ? fieldNameFilter : undefined,
        q: searchQ || undefined,
        sort: sortKey,
        order: sortOrder,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10);
      a.download = `voter-deltas_${selectedBatchId}_${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 413) {
        // The contract: 413 == too many rows; ask user to narrow.
        // eslint-disable-next-line no-alert
        alert('Export too large — narrow the filters first.');
      } else {
        // eslint-disable-next-line no-alert
        alert(
          status
            ? `Export failed (backend error ${status}).`
            : 'Export failed — could not reach the backend.',
        );
      }
    } finally {
      setExporting(false);
    }
  }, [selectedBatchId, deltaKindFilter, fieldNameFilter, searchQ, sortKey, sortOrder]);

  const handleTopLookup = useCallback(
    async (raw: string) => {
      const v = raw.trim();
      if (!v) return;
      const allDigits = /^\d+$/.test(v);
      if (allDigits) {
        // External-id voter lookup. Open as removed-voter card if no
        // internal voter_id is known; if the very first history row has
        // an internal id we can short-circuit straight into the drawer.
        setTopLookupBusy(true);
        try {
          const rows = await fetchVoterDeltaHistory(v);
          if (rows.length === 0) {
            // eslint-disable-next-line no-alert
            alert(`No change history found for voter ${v}.`);
          } else {
            const withInternal = rows.find((r) => r.voter_id);
            setSelectedVoterName(rows[0].voter_name || v);
            if (withInternal?.voter_id) {
              setSelectedVoterId(withInternal.voter_id);
              setSelectedExternalId(null);
            } else {
              setSelectedVoterId(null);
              setSelectedExternalId(v);
              setExternalHistory(rows); // pre-seed; effect will be a no-op
            }
          }
        } catch (e: any) {
          const status = e?.response?.status;
          // eslint-disable-next-line no-alert
          alert(
            status === 404
              ? `Voter ${v} not found.`
              : status
                ? `Lookup failed (backend error ${status}).`
                : 'Lookup failed — could not reach the backend.',
          );
        } finally {
          setTopLookupBusy(false);
        }
      } else {
        // Free-text → drive the drill-down's q filter and scroll to it.
        setSearchQ(v);
        setPage(1);
        // Run on next frame so the section is mounted in case it was hidden.
        requestAnimationFrame(() => {
          tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    },
    [],
  );

  const compareOptions = useMemo<ImportBatchRead[]>(
    () => sortedBatches.filter((b) => b.import_batch_id !== selectedBatchId),
    [sortedBatches, selectedBatchId],
  );

  // ============================================================================
  // Render
  // ============================================================================
  return (
    <div className="space-y-6">
      {/* Header */}
      <ViewHeader
        title={
          <span className="flex items-center gap-2">
            <GitCompare className="w-6 h-6 text-status-info" />
            Data Changes
          </span>
        }
        tagline="How the voter roll shifted between snapshots"
        actions={
          <div className="flex items-end gap-3 flex-wrap">
            <BatchPicker
              batches={sortedBatches}
              selectedId={selectedBatchId}
              onChange={(id) => {
                setSelectedBatchId(id);
                // Drop the compare selection when the primary batch flips —
                // otherwise we silently end up with stale "compare to self".
                setCompareBatchId(null);
              }}
            />
            {sortedBatches.length >= 2 && (
              <ComparePicker
                options={compareOptions}
                selectedId={compareBatchId}
                onChange={setCompareBatchId}
              />
            )}
          </div>
        }
      />

      {/* Voter lookup at the top of the view */}
      <div className="bg-bg-raised rounded-xl p-4 border border-border-base">
        <VoterLookupBar
          value={topLookup}
          onChange={setTopLookup}
          onSubmit={handleTopLookup}
          busy={topLookupBusy}
        />
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-status-danger-bg/30 rounded-xl p-4 border border-status-danger-bg text-status-danger text-sm flex items-center justify-between gap-3">
          <span>{error}</span>
          <button
            onClick={() => setReloadCounter((n) => n + 1)}
            className="bg-accent hover:bg-accent-hover text-white px-3 py-1.5 rounded text-xs font-bold"
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading state */}
      {loading && !error && <StatsGridSkeleton count={4} />}

      {/* Empty state — no batches at all */}
      {!loading && !error && batches && batches.length === 0 && (
        <EmptyState
          icon={GitCompare}
          title="No imports yet"
          description="The diff loader has not produced any import batches for this tenant. Once a snapshot is processed, the changes will show up here."
        />
      )}

      {/* Main content — only when we have a selected batch + summary */}
      {!loading && !error && summary && selectedBatch && (
        <>
          {/* Cross-batch comparison delta panel */}
          {compareBatchId && (
            <CompareDeltaPanel
              loading={comparisonLoading}
              comparison={comparison}
              onClear={() => setCompareBatchId(null)}
            />
          )}

          {/* Stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard
              label="Voters added"
              termId="data_changes_added"
              value={summary.voters_added}
              tone="success"
            />
            <StatCard
              label="Voters removed"
              termId="data_changes_removed"
              value={summary.voters_removed}
              tone="danger"
            />
            <StatCard
              label="Voters modified"
              termId="data_changes_modified"
              value={summary.voters_modified}
              tone="warning"
            />
            <StatCard
              label="Elections added"
              termId="data_changes_election_added"
              value={summary.elections_added}
              tone="info"
            />
          </div>

          {/* Top-fields bar chart */}
          <Section
            title="Top changed fields"
            subtitle="Number of voter records mutated, by field"
            termId="data_changes_modified"
          >
            {summary.top_fields.length === 0 ? (
              <EmptyState
                icon={GitCompare}
                title="No field-level changes"
                description="This batch only added or removed voters."
                compact
              />
            ) : (
              <div style={{ width: '100%', height: Math.max(200, summary.top_fields.length * 36) }}>
                <ResponsiveContainer>
                  <BarChart
                    data={summary.top_fields.map((f) => ({
                      ...f,
                      label: fieldLabel(f.field_name),
                    }))}
                    layout="vertical"
                    margin={{ top: 8, right: 24, bottom: 8, left: 16 }}
                  >
                    <CartesianGrid stroke="var(--chart-grid)" strokeDasharray="3 3" horizontal={false} />
                    <XAxis
                      type="number"
                      tick={{ fill: 'var(--chart-axis)', fontSize: 12 }}
                      stroke="var(--chart-axis)"
                    />
                    <YAxis
                      type="category"
                      dataKey="label"
                      tick={{ fill: 'var(--chart-axis)', fontSize: 12 }}
                      stroke="var(--chart-axis)"
                      width={140}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgb(var(--bg-elevated))',
                        border: '1px solid var(--chart-grid)',
                      }}
                      formatter={(value: number) => value.toLocaleString()}
                    />
                    <Bar dataKey="count" fill={COLOR_FIELD_BAR}>
                      <LabelList
                        dataKey="count"
                        position="right"
                        style={{ fill: 'var(--chart-tick)', fontSize: 11 }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Section>

          {/* Status transitions matrix */}
          <Section
            title="Status transitions"
            subtitle="Where voters moved between Active and Inactive in this batch"
            termId="active_voter"
          >
            <StatusMatrix
              matrix={statusMatrix}
              max={statusMatrixMax}
            />
          </Section>

          {/* Party transitions stacked bar */}
          <Section
            title="Party transitions"
            subtitle="For each starting party, what voters changed to"
            termId="party_component"
          >
            {partyChartData.rows.length === 0 ? (
              <EmptyState
                icon={GitCompare}
                title="No party changes"
                description="No voters changed party affiliation in this batch."
                compact
              />
            ) : (
              <div style={{ width: '100%', height: 280 }}>
                <ResponsiveContainer>
                  <BarChart
                    data={partyChartData.rows}
                    margin={{ top: 8, right: 16, bottom: 8, left: 8 }}
                  >
                    <CartesianGrid stroke="var(--chart-grid)" strokeDasharray="3 3" />
                    <XAxis
                      dataKey="from_value"
                      tick={{ fill: 'var(--chart-axis)', fontSize: 12 }}
                      stroke="var(--chart-axis)"
                    />
                    <YAxis
                      tick={{ fill: 'var(--chart-axis)', fontSize: 12 }}
                      stroke="var(--chart-axis)"
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgb(var(--bg-elevated))',
                        border: '1px solid var(--chart-grid)',
                      }}
                      formatter={(value: number, name: string, ctx: any) => {
                        const from = ctx?.payload?.from_value;
                        const reason = partyChartData.reasons[`${from}->${name}`];
                        const suffix = reason ? ` (${reason})` : '';
                        return [`${value.toLocaleString()}${suffix}`, `→ ${name}`];
                      }}
                    />
                    {partyChartData.toValues.map((to) => (
                      <Bar
                        key={to}
                        dataKey={to}
                        stackId="party"
                        fill={partyColor(to)}
                        name={to}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Section>

          {/* Elections added table */}
          <Section
            title="Elections added"
            subtitle="New elections that joined the roster in this batch"
            termId="data_changes_election_added"
          >
            {!sortedElectionsAdded || sortedElectionsAdded.length === 0 ? (
              <EmptyState
                icon={GitCompare}
                title="No new elections"
                description="No new elections were introduced in this snapshot."
                compact
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border-base">
                      <SortTh
                        label="Date"
                        active={electionsSortKey === 'date'}
                        order={electionsSortOrder}
                        onClick={() => toggleElectionsSort('date')}
                      />
                      <SortTh
                        label="Type"
                        active={electionsSortKey === 'type'}
                        order={electionsSortOrder}
                        onClick={() => toggleElectionsSort('type')}
                      />
                      <SortTh
                        label="Voter count"
                        align="right"
                        active={electionsSortKey === 'count'}
                        order={electionsSortOrder}
                        onClick={() => toggleElectionsSort('count')}
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedElectionsAdded.map((e) => (
                      <tr
                        key={`${e.election_date}-${e.election_type}`}
                        className="border-b border-border-base hover:bg-bg-raised/60"
                      >
                        <td className="py-2 px-3 text-fg-secondary tabular-nums">
                          {formatDate(e.election_date)}
                        </td>
                        <td className="py-2 px-3 text-fg-secondary">{e.election_type}</td>
                        <td className="py-2 px-3 text-right text-fg-primary font-bold tabular-nums">
                          {e.voter_count.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          {/* Geographic concentration of changes */}
          <Section
            title="Where the changes are concentrated"
            subtitle="Modified-voter density by precinct in this batch"
            tipNode={
              <InfoTip
                term={{
                  title: 'Change concentration map',
                  short:
                    'Color = number of modified voters per precinct in the selected batch. Darker = more change.',
                }}
              />
            }
          >
            {selectedBatchId && <DataChangesMap importBatchId={selectedBatchId} />}
          </Section>

          {/* Voter delta drill-down */}
          <div ref={tableRef}>
            <Section
              title="Voter changes"
              subtitle="Per-voter breakdown of what changed in this batch"
              termId="data_changes_modified"
            >
              <div className="space-y-3">
                <SearchInput
                  value={searchQ}
                  onChange={setSearchQ}
                  placeholder="Search by name or external ID…"
                />

                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <FilterBar>
                    <FilterDateRange
                      value={dateRange}
                      onChange={(range) => setDateRange(range)}
                    />
                    <FilterMultiSelect
                      label="Kind"
                      values={deltaKindFilter}
                      options={DELTA_KIND_OPTIONS}
                      onChange={(values) => setDeltaKindFilter(values as DeltaKind[])}
                    />
                    <FilterMultiSelect
                      label="Field"
                      values={fieldNameFilter}
                      options={FIELD_NAME_OPTIONS}
                      onChange={(values) => setFieldNameFilter(values)}
                    />
                  </FilterBar>

                  <button
                    type="button"
                    onClick={handleExportCsv}
                    disabled={exporting}
                    className="inline-flex items-center gap-1.5 rounded bg-bg-elevated hover:bg-border-subtle disabled:opacity-50 disabled:cursor-not-allowed text-fg-primary text-xs font-semibold px-3 py-2"
                    title="Export filtered rows to CSV"
                  >
                    <Download className="w-3.5 h-3.5" aria-hidden="true" />
                    {exporting ? 'Preparing…' : 'Export CSV'}
                  </button>
                </div>
              </div>

              <VoterDeltaTable
                loading={voterLoading}
                error={voterError}
                envelope={voterPage}
                page={page}
                pageSize={pageSize}
                onPrev={() => setPage((p) => Math.max(1, p - 1))}
                onNext={() => setPage((p) => p + 1)}
                sortKey={sortKey}
                sortOrder={sortOrder}
                onToggleSort={toggleSort}
                onClearFilters={clearFilters}
                onOpenVoter={openVoter}
              />
            </Section>
          </div>
        </>
      )}

      {/* Drawer or removed-voter fallback card */}
      <VoterDetailDrawer
        voterId={selectedVoterId}
        onClose={closeVoter}
        onJumpToRoute={onJumpToRoute}
      />
      {selectedExternalId && selectedVoterId === null && (
        <RemovedVoterCard
          externalId={selectedExternalId}
          name={selectedVoterName ?? selectedExternalId}
          loading={externalHistoryLoading}
          error={externalHistoryError}
          history={externalHistory}
          onClose={closeVoter}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function BatchPicker({
  batches,
  selectedId,
  onChange,
}: {
  batches: ImportBatchRead[];
  selectedId: string | null;
  onChange: (id: string) => void;
}) {
  const selected = batches.find((b) => b.import_batch_id === selectedId);
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold flex items-center">
        Import batch
        <InfoTip termId="import_batch" compact />
      </span>
      <select
        value={selectedId ?? ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={batches.length === 0}
        className="bg-bg-base border border-border-base rounded px-2 py-1.5 text-fg-primary text-sm min-w-[260px] disabled:opacity-50"
      >
        {batches.length === 0 && <option value="">No batches</option>}
        {batches.map((b) => (
          <option key={b.import_batch_id} value={b.import_batch_id}>
            {formatObservedAt(b.observed_at)} · {b.total_deltas.toLocaleString()} changes
          </option>
        ))}
      </select>
      {selected && (
        <span className="text-[10px] text-fg-muted">
          observed {formatObservedAt(selected.observed_at)}
        </span>
      )}
    </label>
  );
}

function ComparePicker({
  options,
  selectedId,
  onChange,
}: {
  options: ImportBatchRead[];
  selectedId: string | null;
  onChange: (id: string | null) => void;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold">
        Compare to
      </span>
      <select
        value={selectedId ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        className="bg-bg-base border border-border-base rounded px-2 py-1.5 text-fg-primary text-sm min-w-[220px]"
      >
        <option value="">— none —</option>
        {options.map((b) => (
          <option key={b.import_batch_id} value={b.import_batch_id}>
            {formatObservedAt(b.observed_at)}
          </option>
        ))}
      </select>
    </label>
  );
}

function CompareDeltaPanel({
  loading,
  comparison,
  onClear,
}: {
  loading: boolean;
  comparison: BatchComparison | null;
  onClear: () => void;
}) {
  if (loading) {
    return (
      <div className="bg-bg-raised rounded-xl p-4 border border-border-base">
        <Skeleton className="h-6 w-2/3" />
      </div>
    );
  }
  if (!comparison) return null;

  const cells: Array<{
    label: string;
    current: number;
    delta: number;
    tone: 'success' | 'danger' | 'warning' | 'info';
    invertSign?: boolean; // if true, positive delta is "more removed" → red
  }> = [
    { label: 'Voters added', current: comparison.to_batch.voters_added, delta: comparison.delta.voters_added, tone: 'success' },
    { label: 'Voters removed', current: comparison.to_batch.voters_removed, delta: comparison.delta.voters_removed, tone: 'danger', invertSign: true },
    { label: 'Voters modified', current: comparison.to_batch.voters_modified, delta: comparison.delta.voters_modified, tone: 'warning' },
    { label: 'Elections added', current: comparison.to_batch.elections_added, delta: comparison.delta.elections_added, tone: 'info' },
  ];

  return (
    <div className="bg-bg-raised rounded-xl p-4 border border-border-base">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="text-sm text-fg-secondary">
          Comparing{' '}
          <span className="text-fg-primary font-semibold">
            {formatObservedAt(comparison.to_batch.observed_at)}
          </span>{' '}
          vs{' '}
          <span className="text-fg-primary font-semibold">
            {formatObservedAt(comparison.from_batch.observed_at)}
          </span>
        </div>
        <button
          onClick={onClear}
          className="text-xs text-fg-muted hover:text-fg-primary"
        >
          Clear comparison
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cells.map((c) => (
          <CompareCell key={c.label} {...c} />
        ))}
      </div>
    </div>
  );
}

function CompareCell({
  label,
  current,
  delta,
  invertSign,
}: {
  label: string;
  current: number;
  delta: number;
  tone: 'success' | 'danger' | 'warning' | 'info';
  invertSign?: boolean;
}) {
  const positive = delta > 0;
  const negative = delta < 0;
  // If invertSign (removed metric), more = bad (red), less = good (green).
  // For modified, neutral.
  const color = (() => {
    if (delta === 0) return 'text-fg-secondary';
    const tone = invertSign ? !positive : positive;
    if (label === 'Voters modified') return 'text-fg-secondary';
    return tone ? 'text-status-success' : 'text-status-danger';
  })();
  const sign = positive ? '+' : '';
  return (
    <div className="rounded-lg border border-border-base bg-bg-base/40 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-fg-muted">{label}</div>
      <div className="mt-0.5 text-2xl font-bold text-fg-primary tabular-nums">
        {current.toLocaleString()}
      </div>
      <div className={`text-xs font-semibold tabular-nums ${color}`}>
        {delta === 0 ? '—' : `${sign}${delta.toLocaleString()} vs prior`}
      </div>
    </div>
  );
}

function StatCard({
  label,
  termId,
  value,
  tone,
}: {
  label: string;
  termId?: string;
  value: number;
  tone: 'success' | 'danger' | 'warning' | 'info';
}) {
  const palette: Record<string, { bg: string; border: string; text: string }> = {
    success: {
      bg: 'bg-status-success-bg/25',
      border: 'border-status-success-bg/50',
      text: 'text-status-success',
    },
    danger: {
      bg: 'bg-status-danger-bg/25',
      border: 'border-status-danger-bg/50',
      text: 'text-status-danger',
    },
    warning: {
      bg: 'bg-status-warning-bg/25',
      border: 'border-status-warning-bg/50',
      text: 'text-status-warning',
    },
    info: {
      bg: 'bg-status-info-bg/25',
      border: 'border-status-info-bg/50',
      text: 'text-status-info',
    },
  };
  const p = palette[tone];
  return (
    <div className={`rounded-lg border px-3 py-3 ${p.bg} ${p.border}`}>
      <div className={`text-[10px] uppercase tracking-wider ${p.text} flex items-center`}>
        {label}
        {termId && <InfoTip termId={termId} compact />}
      </div>
      <div className="mt-0.5 text-3xl font-bold text-fg-primary tabular-nums">
        {value.toLocaleString()}
      </div>
    </div>
  );
}

function Section({
  title,
  subtitle,
  termId,
  tipNode,
  children,
}: {
  title: string;
  subtitle?: string;
  termId?: string;
  tipNode?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-bg-raised rounded-xl p-6 border border-border-base space-y-4">
      <div>
        <h2 className="text-lg font-bold text-fg-primary flex items-center">
          {title}
          {termId && <InfoTip termId={termId} compact />}
          {tipNode}
        </h2>
        {subtitle && <p className="text-xs text-fg-muted mt-1">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function StatusMatrix({
  matrix,
  max,
}: {
  matrix: Record<string, Record<string, { count: number; reason: string | null }>>;
  max: number;
}) {
  return (
    <div className="inline-block">
      <div
        className="grid gap-1"
        style={{
          gridTemplateColumns: 'auto repeat(2, minmax(120px, 1fr))',
        }}
      >
        {/* Top-left blank */}
        <div />
        {/* Column headers */}
        {STATUS_VALUES.map((to) => (
          <div
            key={`col-${to}`}
            className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold text-center pb-1"
          >
            → {to}
          </div>
        ))}

        {/* Rows */}
        {STATUS_VALUES.map((from) => (
          <React.Fragment key={`row-${from}`}>
            <div className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold pr-2 self-center">
              {from} →
            </div>
            {STATUS_VALUES.map((to) => {
              const cell = matrix[from][to];
              const cls = intensityClass(cell.count, max);
              const tooltip = cell.reason
                ? `${cell.count} (${cell.reason})`
                : `${cell.count}`;
              return (
                <div
                  key={`cell-${from}-${to}`}
                  title={tooltip}
                  className={`rounded px-3 py-4 text-center font-bold text-fg-primary tabular-nums ${cls}`}
                >
                  {cell.count.toLocaleString()}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

/**
 * Reusable sortable <th>. Local to this view so the elections table
 * (client-side sort) and the drill-down (server-side sort) can share
 * the chevron convention without dragging in the bigger filter SortHeader.
 */
function SortTh({
  label,
  active,
  order,
  onClick,
  align = 'left',
  termNode,
}: {
  label: string;
  active: boolean;
  order: 'asc' | 'desc';
  onClick: () => void;
  align?: 'left' | 'right';
  termNode?: React.ReactNode;
}) {
  return (
    <th
      onClick={onClick}
      aria-sort={active ? (order === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={`py-2 px-3 cursor-pointer select-none transition-colors ${
        align === 'right' ? 'text-right' : 'text-left'
      } ${active ? 'text-status-success font-semibold' : 'text-fg-muted hover:text-fg-secondary'}`}
    >
      <span
        className={`inline-flex items-center gap-1.5 ${
          align === 'right' ? 'justify-end w-full' : ''
        }`}
      >
        {label}
        {active && (
          <span className="text-status-success font-bold" aria-hidden="true">
            {order === 'asc' ? '▲' : '▼'}
          </span>
        )}
        {termNode}
      </span>
    </th>
  );
}

function VoterDeltaTable({
  loading,
  error,
  envelope,
  page,
  pageSize,
  onPrev,
  onNext,
  sortKey,
  sortOrder,
  onToggleSort,
  onClearFilters,
  onOpenVoter,
}: {
  loading: boolean;
  error: string | null;
  envelope: ListEnvelope<VoterDeltaRead> | null;
  page: number;
  pageSize: number;
  onPrev: () => void;
  onNext: () => void;
  sortKey: VoterDeltaSortKey;
  sortOrder: 'asc' | 'desc';
  onToggleSort: (k: VoterDeltaSortKey) => void;
  onClearFilters: () => void;
  onOpenVoter: (row: VoterDeltaRead) => void;
}) {
  if (error) {
    return (
      <div className="bg-status-danger-bg/30 rounded p-3 border border-status-danger-bg text-status-danger text-sm">
        {error}
      </div>
    );
  }

  if (loading && !envelope) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-10" />
        ))}
      </div>
    );
  }

  if (!envelope || envelope.items.length === 0) {
    return (
      <EmptyState
        icon={GitCompare}
        title="No changes match your filters"
        description="Loosen the filters or clear them to see every change in this batch."
        action={{ label: 'Clear filters', onClick: onClearFilters }}
        compact
      />
    );
  }

  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, envelope.total);
  const hasNext = page * pageSize < envelope.total;
  const hasPrev = page > 1;

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border-base">
              <SortTh
                label="Voter"
                active={sortKey === 'voter_name'}
                order={sortOrder}
                onClick={() => onToggleSort('voter_name')}
              />
              <SortTh
                label="Kind"
                active={sortKey === 'delta_kind'}
                order={sortOrder}
                onClick={() => onToggleSort('delta_kind')}
              />
              <SortTh
                label="Field"
                active={sortKey === 'field_name'}
                order={sortOrder}
                onClick={() => onToggleSort('field_name')}
              />
              <th className="py-2 px-3 text-left text-fg-muted">Old → New</th>
              <th className="py-2 px-3 text-left text-fg-muted">
                <span className="inline-flex items-center">
                  Reason
                  <InfoTip termId="data_changes_change_reason" compact />
                </span>
              </th>
              <SortTh
                label="Observed"
                active={sortKey === 'observed_at'}
                order={sortOrder}
                onClick={() => onToggleSort('observed_at')}
              />
            </tr>
          </thead>
          <tbody>
            {envelope.items.map((row) => (
              <tr
                key={row.id}
                className="border-b border-border-base hover:bg-bg-raised/60"
              >
                <td className="py-2 px-3">
                  <button
                    type="button"
                    onClick={() => onOpenVoter(row)}
                    className="text-left group"
                  >
                    <div className="text-fg-primary font-medium group-hover:text-status-success group-hover:underline">
                      {row.voter_name || '—'}
                    </div>
                    <div className="text-[11px] text-fg-muted font-mono">
                      {row.voter_id_external}
                    </div>
                  </button>
                </td>
                <td className="py-2 px-3">
                  <DeltaKindBadge kind={row.delta_kind} />
                </td>
                <td className="py-2 px-3 text-fg-secondary">{fieldLabel(row.field_name)}</td>
                <td className="py-2 px-3 text-fg-secondary">
                  {row.delta_kind === 'modified' ? (
                    <span>
                      <span className="text-fg-muted">{row.old_value ?? '—'}</span>
                      <span className="text-fg-muted mx-1">→</span>
                      <span className="text-fg-primary font-medium">{row.new_value ?? '—'}</span>
                    </span>
                  ) : row.delta_kind === 'added' ? (
                    <span className="text-status-success">+ {row.new_value ?? 'voter'}</span>
                  ) : row.delta_kind === 'removed' ? (
                    <span className="text-status-danger">− {row.old_value ?? 'voter'}</span>
                  ) : (
                    <span className="text-status-info">{row.new_value ?? '—'}</span>
                  )}
                </td>
                <td className="py-2 px-3 text-fg-muted text-xs">
                  {row.change_reason ?? '—'}
                </td>
                <td className="py-2 px-3 text-fg-muted text-xs tabular-nums">
                  {formatObservedAt(row.observed_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between gap-2 pt-2 text-xs text-fg-muted">
        <div>
          Showing {start.toLocaleString()}–{end.toLocaleString()} of{' '}
          {envelope.total.toLocaleString()}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onPrev}
            disabled={!hasPrev || loading}
            className="px-3 py-1.5 rounded bg-bg-elevated hover:bg-border-subtle disabled:opacity-40 disabled:cursor-not-allowed text-fg-primary"
            title="Previous page"
          >
            ← Prev
          </button>
          <span className="tabular-nums">Page {page}</span>
          <button
            onClick={onNext}
            disabled={!hasNext || loading}
            className="px-3 py-1.5 rounded bg-bg-elevated hover:bg-border-subtle disabled:opacity-40 disabled:cursor-not-allowed text-fg-primary"
            title="Next page"
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}

function DeltaKindBadge({ kind }: { kind: DeltaKind }) {
  const map: Record<DeltaKind, { bg: string; text: string; label: string }> = {
    added: {
      bg: 'bg-status-success-bg/40 border-status-success-bg/50',
      text: 'text-status-success',
      label: 'Added',
    },
    removed: {
      bg: 'bg-status-danger-bg/40 border-status-danger-bg/50',
      text: 'text-status-danger',
      label: 'Removed',
    },
    modified: {
      bg: 'bg-status-warning-bg/40 border-status-warning-bg/50',
      text: 'text-status-warning',
      label: 'Modified',
    },
    election_added: {
      bg: 'bg-status-info-bg/40 border-status-info-bg/50',
      text: 'text-status-info',
      label: 'Election',
    },
  };
  const m = map[kind];
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded border text-[11px] font-bold uppercase tracking-wide ${m.bg} ${m.text}`}
    >
      {m.label}
    </span>
  );
}

/**
 * VoterLookupBar — top-of-view search field. Submits on Enter or button
 * click; the parent decides whether to open the drawer (digit input) or
 * push the value into the drill-down's q filter (free-text).
 */
function VoterLookupBar({
  value,
  onChange,
  onSubmit,
  busy,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: (v: string) => void;
  busy: boolean;
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(value);
      }}
      className="flex items-center gap-2"
    >
      <Search className="w-4 h-4 text-fg-muted" aria-hidden="true" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Look up a voter by ID or name…"
        aria-label="Look up a voter"
        className="flex-1 bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary placeholder-fg-muted text-sm"
      />
      <button
        type="submit"
        disabled={busy || !value.trim()}
        className="px-3 py-2 rounded bg-status-success-bg hover:bg-status-success-bg/80 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold"
      >
        {busy ? 'Looking up…' : 'Find'}
      </button>
    </form>
  );
}

/**
 * RemovedVoterCard — the fallback for voters that no longer have an
 * internal `voter_id`. The full drawer needs a /voters/{id} record;
 * removed voters don't get one, so we render their change history
 * directly from the data-changes endpoint instead.
 */
function RemovedVoterCard({
  externalId,
  name,
  loading,
  error,
  history,
  onClose,
}: {
  externalId: string;
  name: string;
  loading: boolean;
  error: string | null;
  history: VoterDeltaRead[] | null;
  onClose: () => void;
}) {
  return (
    <>
      <div
        onClick={onClose}
        className="fixed inset-0 bg-black/50 z-40"
        aria-hidden="true"
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Removed voter history"
        className="fixed inset-y-0 right-0 w-full max-w-md bg-bg-base border-l border-border-base shadow-2xl z-50 flex flex-col"
      >
        <div className="flex items-start justify-between gap-3 p-4 border-b border-border-base bg-bg-raised/60">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-status-warning font-semibold mb-0.5">
              Removed voter
            </div>
            <h2 className="text-xl font-bold text-fg-primary truncate">{name}</h2>
            <div className="text-xs text-fg-muted mt-0.5 truncate">
              ID {externalId}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="flex-shrink-0 w-8 h-8 inline-flex items-center justify-center rounded-md text-fg-muted hover:text-fg-primary hover:bg-bg-elevated"
          >
            ×
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <div className="rounded-lg border border-status-warning-bg/40 bg-status-warning-bg/20 p-3 text-sm text-status-warning">
            This voter is no longer present in the latest snapshot, so their
            full profile is not available. Below is every recorded change to
            this external ID across all import batches.
          </div>

          {loading && (
            <div className="space-y-2">
              <Skeleton className="h-10" />
              <Skeleton className="h-10" />
              <Skeleton className="h-10" />
            </div>
          )}

          {error && !loading && (
            <div className="rounded-lg border border-status-danger-bg bg-status-danger-bg/30 p-3 text-sm text-status-danger">
              {error}
            </div>
          )}

          {!loading && !error && history && history.length === 0 && (
            <div className="text-sm text-fg-muted">No change history found for this voter.</div>
          )}

          {!loading && !error && history && history.length > 0 && (
            <div className="space-y-2">
              {history.map((r) => (
                <div
                  key={r.id}
                  className="rounded border border-border-base bg-bg-raised p-3"
                >
                  <div className="flex items-center justify-between gap-2">
                    <DeltaKindBadge kind={r.delta_kind} />
                    <span className="text-[11px] text-fg-muted tabular-nums">
                      {formatObservedAt(r.observed_at)}
                    </span>
                  </div>
                  <div className="text-sm text-fg-secondary mt-2">
                    <span className="text-fg-muted">{fieldLabel(r.field_name)}: </span>
                    {r.delta_kind === 'modified' ? (
                      <span>
                        <span className="text-fg-muted">{r.old_value ?? '—'}</span>
                        <span className="text-fg-muted mx-1">→</span>
                        <span className="text-fg-primary font-medium">{r.new_value ?? '—'}</span>
                      </span>
                    ) : (
                      <span className="text-fg-primary">{r.new_value ?? r.old_value ?? '—'}</span>
                    )}
                  </div>
                  {r.change_reason && (
                    <div className="text-[11px] text-fg-muted mt-1">{r.change_reason}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </aside>
    </>
  );
}

export default DataChangesView;
